import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { IParams, ISaveExpenseType, Page, Widget } from '../../../shared';
import { ExpenseTypeInputs } from '../../components';
import { expenseTypeFromFormMapper } from '../../mappers';
import { useExpenseTypeSchema } from '../../validators';
import { useExpenseType, useSaveExpenseType } from '../../hooks/expense-type.hook';

export const ExpenseTypeEditPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>() as IParams;

    const { data: expenseType, isLoading } = useExpenseType(id);
    const { mutateAsync: saveExpenseType, isPending } = useSaveExpenseType();

    const schema = useExpenseTypeSchema();
    const form = useForm<ISaveExpenseType>({
        resolver: yupResolver(schema),
        mode: 'onSubmit',
    });

    useEffect(() => {
        if (expenseType) form.reset(expenseType);
    }, [form, expenseType]);

    const onSubmit = useCallback(
        async (data: ISaveExpenseType) => {
            await saveExpenseType({ data: expenseTypeFromFormMapper(data) });
            navigate('/admin/expense-types');
        },
        [saveExpenseType, navigate],
    );

    const actions = useMemo(
        () => [
            <Button onClick={() => navigate('/admin/expense-types')} color="secondary">
                {t('cancel')}
            </Button>,
            <Button variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
                {t('save')}
            </Button>,
        ],
        [form, onSubmit, isPending, t, navigate],
    );

    return (
        <Page
            title={<Box width="300px">{id ? t('editExpenseType') : t('newExpenseType')}</Box>}
            actions={actions}
            onBack={() => navigate(-1)}
            loading={isLoading}
        >
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Widget footer={[...actions].reverse()}>
                        <ExpenseTypeInputs />
                    </Widget>
                </form>
            </FormProvider>
        </Page>
    );
};
